import { t } from "@lingui/core/macro";
import { setUser as setSentryUser } from "@sentry/react";
import { fetchAuthSession, getCurrentUser, signOut, type AuthUser } from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import { usePostHog } from "posthog-js/react";
import { createContext, type ReactNode, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";

import { signOutUser as signOutUserAnalytics } from "@/redux/analytics/analyticsReducers";
import { setCognitoId, setEmail, signOutUser as signOutUserApp } from "@/redux/app/appReducers";
import AuthPage from "@/routes/_unauthenticated/-components/AuthPage";
import { api } from "@/shared/api/api";
import { Button } from "@/shared/components/ui/button";

import LoadingSpinner from "./LoadingSpinner";

export type AuthUserWithAttributes = AuthUser & {
  attributes: {
    email: string;
    sub: string;
  };
};

type AuthenticatorProps = {
  children: ReactNode;
};

type ContextType = {
  user: AuthUserWithAttributes;
  signOut: () => void;
};

export const AuthenticatorContext = createContext<ContextType>({
  user: {} as AuthUserWithAttributes,
  signOut: () => {},
});

const AUTHENTICATION_REFRESH_ERRORS = [
  "NotAuthorizedException",
  "TokenRevokedException",
  "UserNotFoundException",
  "PasswordResetRequiredException",
  "UserNotConfirmedException",
  "RefreshTokenReuseException",
] as const;

const getErrorName = (error: unknown) => {
  if (!error || typeof error !== "object" || !("name" in error)) return "";
  const errorName = error.name;
  return typeof errorName === "string" ? errorName : "";
};

const isAuthenticationRefreshError = (error: unknown) => {
  const errorName = getErrorName(error);
  return AUTHENTICATION_REFRESH_ERRORS.some((authenticationErrorName) => errorName.startsWith(authenticationErrorName));
};

const isUnauthenticatedError = (error: unknown) =>
  getErrorName(error) === "UserUnAuthenticatedException" || isAuthenticationRefreshError(error);

export default function Authenticator({ children }: AuthenticatorProps) {
  const posthog = usePostHog();
  const [loading, setLoading] = useState(true);
  const [sessionError, setSessionError] = useState(false);
  const [user, setUser] = useState<AuthUserWithAttributes | null>(null);
  const dispatch = useDispatch();

  const loadCurrentUser = async () => {
    setSessionError(false);
    try {
      const currentUser = await getCurrentUser();
      const { tokens } = await fetchAuthSession();
      const email = tokens?.idToken?.payload.email;
      const sub = tokens?.idToken?.payload.sub;
      setUser({
        ...currentUser,
        attributes: {
          email: typeof email === "string" ? email : "",
          sub: typeof sub === "string" ? sub : currentUser.userId,
        },
      });
    } catch (error) {
      if (isUnauthenticatedError(error)) {
        setUser(null);
      } else {
        setSessionError(true);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const unsubscribe = Hub.listen("auth", ({ payload }) => {
      switch (payload.event) {
        case "signedIn":
          setLoading(true);
          void loadCurrentUser();
          break;
        case "signedOut":
          setUser(null);
          setSessionError(false);
          setLoading(false);
          // reset the redux state
          dispatch(api.util.resetApiState());
          dispatch(signOutUserApp());
          dispatch(signOutUserAnalytics());
          break;
        case "tokenRefresh_failure":
          if (!isAuthenticationRefreshError(payload.data?.error)) break;
          setUser(null);
          setSessionError(false);
          setLoading(false);
          dispatch(api.util.resetApiState());
          dispatch(signOutUserApp());
          dispatch(signOutUserAnalytics());
          break;
      }
    });

    void loadCurrentUser();

    return () => unsubscribe();
  }, [dispatch]);

  useEffect(() => {
    if (user) {
      setSentryUser({
        id: user.attributes.sub,
        email: user.attributes.email,
      });
      posthog.identify(user.attributes.sub, {
        email: user.attributes.email,
        env: import.meta.env.VITE_ENV,
      });
      dispatch(setCognitoId(user.attributes.sub));
      dispatch(setEmail(user.attributes.email));
    } else {
      setSentryUser(null);
      posthog.reset();
    }
  }, [dispatch, posthog, user]);

  const contextValue = useMemo(() => ({ user: user as AuthUserWithAttributes, signOut: () => void signOut() }), [user]);

  if (loading) return <LoadingSpinner />;

  if (sessionError) {
    return (
      <AuthPage>
        <p>
          {t`We were unable to sign you in to your account. Please try again later or reach out to support if the issue persists.`}
        </p>
        <Button
          className="mt-4 w-full"
          onClick={() => {
            setLoading(true);
            void loadCurrentUser();
          }}
        >
          {t`Retry`}
        </Button>
      </AuthPage>
    );
  }

  return <AuthenticatorContext.Provider value={contextValue}>{children}</AuthenticatorContext.Provider>;
}
